import { useEffect, useState } from "react";
import { Share2, Copy, Download, Loader2, Music2, Megaphone } from "lucide-react";
import { api, API, fmtError } from "../lib/api";
import { toast } from "sonner";

const SITE_URL = "https://laconsoledesartistes.fr";

const captions = (t) => {
  // Lien de PARTAGE (balises OG côté serveur) : la carte Facebook affiche jaquette + titre + artiste
  const link = `${SITE_URL}/api/share/track/${t.track_id}`;
  const artist = t.artist || "";
  return [
    {
      key: "facebook",
      label: "Facebook",
      text: `Nouveau titre disponible en licence : « ${t.title} » — ${artist}\n\nMusique libre de droit pour vos pubs, films et contenus. Écoute et licence en ligne :\n${link}`,
    },
    {
      key: "instagram",
      label: "Instagram",
      text: `« ${t.title} » — ${artist}\nMusique libre de droit, licence en ligne (lien en bio)\n\n#musiquelibrededroit #musiclicensing #pub #film #sync #productionmusicale #${artist.toLowerCase().replace(/[^a-z0-9]/g, "")}`,
    },
    {
      key: "x",
      label: "X (Twitter)",
      text: `« ${t.title} » — ${artist}\nMusique libre de droit pour l'image. Licence en ligne :\n${link}`,
    },
  ];
};

export const StudioPostKit = () => {
  const [tracks, setTracks] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState("");
  const [metaOk, setMetaOk] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    api.get("/studio/tracks").then((r) => {
      setTracks(r.data || []);
      if (r.data?.length) setSelected(r.data[0].track_id);
    }).catch(() => {});
    // Visible uniquement pour l'admin avec Meta connecté (403 sinon)
    api.get("/admin/meta/status").then((r) => setMetaOk(!!r.data?.connected)).catch(() => {});
  }, []);

  const track = tracks.find((t) => t.track_id === selected);

  const publishNow = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/admin/meta/publish/${track.track_id}`);
      toast.success(`Publié : ${data.fb ? "Facebook ✓ " : ""}${data.ig ? "Instagram ✓" : ""}`.trim());
    } catch (e) {
      toast.error(fmtError(e));
    } finally {
      setPublishing(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`Texte ${label} copié !`);
  };

  const download = async (fmt) => {
    if (!track) return;
    setLoading(fmt);
    try {
      const res = await fetch(`${API}/studio/promo/track/${track.track_id}/${fmt}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `post_${track.title.replace(/\s+/g, "_").toLowerCase()}_${fmt}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec du téléchargement");
    } finally {
      setLoading("");
    }
  };

  const btnCls =
    "flex items-center justify-center gap-2 border border-amber-500/50 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50";

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="studio-postkit-section">
      <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
        <Share2 className="h-5 w-5 text-amber-500" /> Préparer un post
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">
        Choisissez un morceau : le visuel et le texte sont prêts à copier-coller sur Facebook,
        Instagram ou X. La publication affichera automatiquement la jaquette avec votre nom.
      </p>

      <div className="mt-6">
        <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Morceau</label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          data-testid="postkit-track-select"
          className="w-full border border-white/15 bg-black px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
        >
          {tracks.map((t) => (
            <option key={t.track_id} value={t.track_id}>{t.title} — {t.artist}</option>
          ))}
        </select>
      </div>

      {track ? (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="border border-white/[0.06] p-4">
            <p className="mb-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500">Visuels du morceau</p>
            <img
              src={`${API}/studio/promo/track/${track.track_id}/carre`}
              alt={`Visuel ${track.title}`}
              data-testid="postkit-preview"
              className="aspect-square w-full border border-white/10 object-cover"
              loading="lazy"
            />
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button onClick={() => download("carre")} disabled={!!loading} data-testid="postkit-download-carre" className={btnCls}>
                {loading === "carre" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Carré (Insta)
              </button>
              <button onClick={() => download("paysage")} disabled={!!loading} data-testid="postkit-download-paysage" className={btnCls}>
                {loading === "paysage" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Paysage (FB)
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {captions(track).map((c) => (
              <div key={c.key} className="border border-white/[0.06] p-4" data-testid={`postkit-caption-${c.key}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400">{c.label}</p>
                  <button
                    onClick={() => copy(c.text, c.label)}
                    data-testid={`postkit-copy-${c.key}`}
                    className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-amber-400"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copier
                  </button>
                </div>
                <p className="mt-2 whitespace-pre-line break-all text-xs leading-relaxed text-zinc-400">{c.text}</p>
              </div>
            ))}
            <button
              onClick={() => copy(`${SITE_URL}/api/share/track/${track.track_id}`, "lien")}
              data-testid="postkit-copy-link"
              className={`${btnCls} w-full`}
            >
              <Copy className="h-4 w-4" /> Copier le lien du morceau
            </button>
            {metaOk && (
              <button
                onClick={publishNow}
                disabled={publishing}
                data-testid="postkit-publish-now"
                className="flex w-full items-center justify-center gap-2 bg-amber-500 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
              >
                {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
                {publishing ? "Publication…" : "Publier maintenant sur Facebook / Instagram"}
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className="mt-8 flex items-center gap-3 text-sm text-zinc-500" data-testid="postkit-empty">
          <Music2 className="h-4 w-4" /> Publiez d'abord un morceau pour préparer un post.
        </p>
      )}
    </section>
  );
};
